/**
 * A confirm dialog with a reason box (OPEN-144). Required reasons need REASON_MIN characters
 * (the API's rule); an optional one may be blank or REASON_MIN+. Kept as typed (a note, so
 * the DEC-147 uppercase rule does not apply).
 */

import React from 'react';
import { Button } from 'src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { REASON_MIN } from './parked-labels';

export function ReasonDialog({
  open,
  title,
  body,
  confirmLabel,
  required,
  pending,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  body: React.ReactNode;
  confirmLabel: string;
  required: boolean;
  pending: boolean;
  onConfirm: (reason: string) => void;
  onClose: () => void;
}): React.JSX.Element {
  const [reason, setReason] = React.useState('');
  const trimmed = reason.trim();
  const valid = required
    ? trimmed.length >= REASON_MIN
    : trimmed.length === 0 || trimmed.length >= REASON_MIN;

  React.useEffect(() => {
    if (!open) setReason('');
  }, [open]);

  return (
    <Dialog
      open={open}
      onOpenChange={(o): void => {
        if (!o) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div style={{ fontSize: '14px', marginBottom: '8px' }}>{body}</div>
        <textarea
          aria-label={`${confirmLabel} reason`}
          placeholder={required ? 'Reason (required)' : 'Reason (optional)'}
          rows={3}
          value={reason}
          onChange={(e): void => {
            setReason(e.target.value);
          }}
          style={{
            width: '100%',
            borderRadius: '10px',
            border: '1px solid var(--color-border-secondary)',
            backgroundColor: 'var(--color-bg-tertiary)',
            color: 'var(--color-text-primary)',
            padding: '8px',
            fontSize: '14px',
          }}
        />
        {!valid && trimmed.length > 0 && (
          <p style={{ fontSize: '12px', color: 'var(--color-danger)' }}>
            At least {REASON_MIN} characters.
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button
            disabled={!valid || pending}
            onClick={(): void => {
              onConfirm(reason);
            }}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
